import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

/**
 * Shared brand shimmer text (same treatment as Nestly wordmark).
 * Hover: yoyo via `is-shimmering`. prefers-reduced-motion: solid text.
 * No Nestly lockup slash — that lives only on Logo.
 */
export default function BrandShimmerText({
  children,
  className,
  as: Tag = "span",
  boot = false,
  ...rest
}) {
  const shimmerRef = useRef(null);
  const [reduceMotion, setReduceMotion] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduceMotion(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const startShineLoop = useCallback(() => {
    if (reduceMotion) return;
    const el = shimmerRef.current;
    if (!el) return;
    el.classList.remove("is-shimmering");
    void el.offsetWidth;
    el.classList.add("is-shimmering");
  }, [reduceMotion]);

  const stopShineLoop = useCallback(() => {
    const el = shimmerRef.current;
    if (!el) return;
    el.classList.remove("is-shimmering");
  }, []);

  useEffect(() => {
    if (!boot) return undefined;
    const el = shimmerRef.current;
    if (!el || reduceMotion) return undefined;
    el.classList.add("brand-shimmer--boot");
    const onEnd = (event) => {
      if (event.target !== el) return;
      if (event.animationName === "brand-shimmer-load") {
        el.classList.remove("brand-shimmer--boot");
      }
    };
    el.addEventListener("animationend", onEnd);
    return () => el.removeEventListener("animationend", onEnd);
  }, [boot, reduceMotion]);

  return (
    <Tag
      className={cn("inline-flex items-center text-stone2-dark", className)}
      onMouseEnter={startShineLoop}
      onMouseLeave={stopShineLoop}
      {...rest}
    >
      <span
        ref={shimmerRef}
        className={cn(
          "brand-shimmer leading-none tracking-tight",
          reduceMotion && "brand-shimmer--static"
        )}
        aria-hidden="true"
      >
        {children}
      </span>
    </Tag>
  );
}
